import { HTMLAttributes, useState } from 'react';
import { WithPopover } from './WithPopover';
import { TextOverflowEllipsis } from './TextOverflowEllipsis';

export type TextOverflowProps = HTMLAttributes<HTMLDivElement> & {
  renderIn?: 'popover' | 'tooltip' | null;
  lineClamp?: number;
  popoverAcceptsMouse?: boolean;
  textContent?: string;
  focusable?: boolean;
  showWhitespace?: boolean;
};

/**
 * Wrapper component providing truncation with ellipsis and a popover for your content.
 * @component
 * @param textContent use this prop when you have nested elements (such as icons) contained within your to-be-truncated content, and wish to avoid rendering the text they contain.
 */
export const TextOverflow = ({
  className,
  children,
  lineClamp,
  renderIn = 'popover',
  popoverAcceptsMouse = true,
  textContent,
  focusable = false,
  showWhitespace = false,
  ...rest
}: TextOverflowProps) => {
  const showPopover = renderIn === 'popover';
  const showTooltip = renderIn === 'tooltip';
  const [isTextOverflowed, setIsTextOverflowed] = useState<boolean>(false);
  const [innerText, setInnerText] = useState<string>('');
  const overflowText = textContent ?? innerText;

  const ellipsisProps = {
    className,
    lineClamp,
    onEllipsisChange: setIsTextOverflowed,
    onInnerTextChange: setInnerText,
    ...rest,
  };

  return (
    <WithPopover
      acceptsMouse={popoverAcceptsMouse}
      content={showPopover && isTextOverflowed && overflowText}
    >
      <TextOverflowEllipsis
        {...(showTooltip && isTextOverflowed
          ? { 'data-tooltip-text': overflowText }
          : {})}
        {...ellipsisProps}
        focusable={focusable}
        showWhitespace={showWhitespace}
      >
        {children}
      </TextOverflowEllipsis>
    </WithPopover>
  );
};
